import { Upload } from 'lucide-react';
import { useId, useRef, useState, type DragEvent } from 'react';
import { cx } from '../cx.js';

export interface FileDropProps {
  label: string;
  /** Spiega formati e dimensioni, es. "CSV o Excel, fino a 5 MB". */
  hint?: string;
  accept?: string;
  onFile: (file: File) => void;
  file?: File | null;
  error?: string;
  testoScegli?: string;
  className?: string;
}

/** Area di caricamento file: trascina oppure scegli dal disco. Accessibile da tastiera tramite il bottone. */
export function FileDrop({ label, hint, accept, onFile, file, error, testoScegli = 'Scegli un file', className }: FileDropProps) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const [sopra, setSopra] = useState(false);

  function gestisciDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setSopra(false);
    const f = e.dataTransfer.files[0];
    if (f) onFile(f);
  }

  return (
    <div className={cx('flex flex-col gap-2', className)}>
      <span className="t-label text-ink" id={`${id}-label`}>
        {label}
      </span>
      <div
        role="group"
        aria-labelledby={`${id}-label`}
        onDragOver={(e) => {
          e.preventDefault();
          setSopra(true);
        }}
        onDragLeave={() => setSopra(false)}
        onDrop={gestisciDrop}
        className={cx(
          'rounded-lg border-2 border-dashed p-8 flex flex-col items-center text-center gap-3 bg-surface',
          sopra ? 'border-accent bg-accent-soft' : error ? 'border-error' : 'border-border-strong',
        )}
      >
        <Upload aria-hidden size={32} strokeWidth={1.75} className="text-ink-muted" />
        {file ? <p className="t-body">{file.name}</p> : hint ? <p className="t-body text-ink-muted">{hint}</p> : null}
        <input
          ref={input}
          id={id}
          type="file"
          accept={accept}
          className="sr-only"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onFile(f);
            e.target.value = '';
          }}
        />
        <button
          type="button"
          onClick={() => input.current?.click()}
          className="inline-flex items-center justify-center min-h-[44px] px-6 py-3 rounded-md border border-border-strong bg-surface text-ink t-label hover:bg-surface-sunken"
        >
          {testoScegli}
        </button>
      </div>
      {error && (
        <p role="alert" className="t-body-small text-error">
          {error}
        </p>
      )}
    </div>
  );
}
